import React from "react";
import SEO from "../component/SEO";
import { Card } from "../component/ui/Card";
import { Button } from "../component/ui/Button";
import { Mail, Phone, MapPin, Clock, Sparkles, ArrowRight, Star } from "lucide-react";
import homecake from "../images/homecake.png";
import { useNavigate } from "react-router-dom";
import Testimonials from "../component/HomeReviews";
import OfferSlider from "../component/offers";
import { businessInfo } from "../config/business";

export default function Home() {
  const navigate = useNavigate();
  const handleOrder = () => navigate("/menu");
  const handleContact = () => navigate("/contact");

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-pink-50 via-orange-50 to-purple-50 relative overflow-hidden">
      <SEO 
        title="K Bakery" 
        description="Delicious cakes, brownies, cupcakes and more at K Bakery. Life's messy, but that's what frosting's for!"
        keywords="bakery, cakes, eggless cakes, pastries, brownies, cupcakes, sweet, dessert"
      />
      {/* Floating Background Shapes */}
      <div className="absolute top-20 right-10 w-80 h-80 bg-gradient-to-br from-[#FFB4AB]/40 to-[#E5DEFF]/40 rounded-full blur-3xl float-animation z-0"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-gradient-to-br from-[#E8F4F8]/50 to-[#FFD4A3]/40 rounded-full blur-3xl float-animation" style={{ animationDelay: "2s" }}></div>
      <div className="absolute top-40 left-1/4 w-64 h-64 bg-gradient-to-br from-[#E5DEFF]/50 to-[#FFB4AB]/30 rounded-full blur-3xl float-animation" style={{ animationDelay: "4s" }}></div>

      {/* Hero Section */}
      <section id="home" className="relative pt-20 pb-32">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Hero Text */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#FFB4AB]/10 to-[#E5DEFF]/10 rounded-full border border-[#FFB4AB]/20">
                <Sparkles className="w-4 h-4 text-[#FFB4AB]" />
                <span className="text-sm text-[#2D2D2D]">100% Eggless • Premium Quality</span>
              </div>
              <h1 className="font-serif text-6xl sm:text-7xl lg:text-8xl text-[#2D2D2D] leading-[1.1]">
                Life's
                <span className="block bg-gradient-to-r py-4 from-[#FFB4AB] to-[#E5DEFF] bg-clip-text text-transparent">
                  Messy —
                </span>
              </h1>
              <p className="text-lg md:text-xl text-[#7D7D7D] max-w-xl leading-relaxed">
                But that’s what frosting’s for — covering the chaos and making it taste like a celebration.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button               onClick={handleOrder}
 className="bg-[#FFB4AB] hover:bg-[#FF9E95] text-[#2D2D2D] px-8 h-14 rounded-full group shadow-xl shadow-[#FFB4AB]/20 hover:shadow-2xl hover:shadow-[#FFB4AB]/30 transition-all">
                  Explore Our Menu
                </Button>
                <Button  onClick={handleContact} variant="outline" className="border-2 border-[#2D2D2D]/10 text-[#2D2D2D] hover:bg-[#2D2D2D]/5 px-8 h-14 rounded-full transition-all">
                  Get in Touch
                </Button>
              </div>
               <p className="text-lg md:text-xl font-serif font-normal text-[#443c3c] max-w-xl leading-relaxed">
                “Need it today? Call us and satisfy your sweet tooth in no time!”
              </p>
            </div>

            {/* Hero Image */}
            <div className="block">
              <div className="relative z-10 aspect-square rounded-[4rem] overflow-hidden soft-shadow-sm">
                <picture>
                  <source srcSet={require("../images/homecake.webp")} type="image/webp" />
                  <img
                    src={homecake}
                    alt="Elegant celebration cake"
                    className="w-full h-full object-cover rounded-[4rem]"
                    width="864"
                    height="1184"
                    loading="eager"
                  />
                </picture>
              </div>
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-[#E8F4F8] rounded-full float-animation shadow-xl"></div>
              {/* <div className="absolute -right-8 w-125 bg-gradient-to-br from-[#FFB4AB]/20 to-[#E5DEFF]/20 blob-animation rounded-[4rem]"></div> */}
              <div className="absolute -bottom-8 w-40 h-40 bg-[#FFD4A3]/30 rounded-full float-animation shadow-xl" style={{ animationDelay: "2s" }}></div>
            </div>
          </div>
        </div>
      </section>
      <section className="my-10 px-4 md:px-8">
        <OfferSlider />
      </section>

      {/* Testimonials Section */}
      <Testimonials />

      {/* Contact Section */}
      <section id="contact" className="relative py-20 text-center">
        {/* Floating Blob */}
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-br from-[#FFD4A3]/40 to-[#E5DEFF]/40 rounded-full float-animation z-0" style={{ animationDelay: "1s" }}></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-gradient-to-br from-[#E8F4F8]/50 to-[#FFB4AB]/40 rounded-full  float-animation z-0" style={{ animationDelay: "2s" }}></div>
        <div className="absolute top-40 left-1/4 w-64 h-64 bg-gradient-to-br from-[#E5DEFF]/50 to-[#FFB4AB]/30 rounded-full float-animation z-0" style={{ animationDelay: "1s" }}></div>

        {/* Badge */}
        <div className="inline-flex items-center gap-2 mb-4">
          <span className="h-3 w-3 bg-pink-400 rounded-full animate-pulse"></span>
          <span className="text-sm font-medium font-inter text-gray-700">Get in Touch</span>
        </div>

        {/* Heading */}
        <h2 className="text-6xl md:text-7xl font-serif font-normal text-gray-800">
          Let’s Create{" "}
        </h2>
        <h2 className="text-6xl md:text-7xl m-4 font-serif font-normal text-gray-800">
          <span className="font-script text-pink-400">Something Sweet</span>
        </h2>
        <p className="text-gray-600 mt-8 max-w-2xl mx-auto font-inter">
          Have a special occasion coming up? We’d love to make it memorable with our delicious creations.
        </p>

        {/* Contact Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-6xl mx-auto mt-10 px-6 relative z-10">
          {/* Location */}
          <div className="p-6 bg-[#FFFFFF] rounded-2xl shadow-md flex flex-col items-center hover:shadow-xl transition">
            <MapPin className="text-[#f55b4a] bg-[#fde5e2] rounded-xl p-2 w-10 h-10" />
            <h4 className="mt-3 font-serif font-semibold text-lg">Location</h4>
            <p className="mt-2 text-center font-inter text-gray-700">
              {businessInfo.address.street},<br />
              {businessInfo.address.city}, {businessInfo.address.region}, {businessInfo.address.country},<br/> {businessInfo.address.postalCode}
            </p>
          </div>

          {/* Phone */}
          <div className="p-6 bg-[#FFFFFF] rounded-2xl shadow-md flex flex-col items-center hover:shadow-xl transition">
            <Phone className="text-[#4dc8f1] bg-[#E8F4F8] rounded-xl p-2 w-10 h-10" />
            <h4 className="mt-3 font-serif font-semibold text-lg">Phone</h4>
            <p className="mt-2 text-center font-inter text-gray-700">
               +1 403-360-3664<br />
              Call for custom orders
            </p>
          </div>

          {/* Email */}
          <div className="p-6 bg-[#FFFFFF] rounded-2xl shadow-md flex flex-col items-center hover:shadow-xl transition">
            <Mail className="text-[#24d0ab] bg-[#dfeeeb] rounded-xl p-2 w-10 h-10" />
            <h4 className="mt-3 font-serif font-semibold text-lg">Email</h4>
            <p className="mt-2 text-center font-inter text-gray-700">
              kbakery1002@gmail.com<br />
              We’d love to hear from you
            </p>
          </div>

          {/* Hours */}
          <div className="p-6 bg-[#FFFFFF] rounded-2xl shadow-md flex flex-col items-center hover:shadow-xl transition">
            <Clock className="text-[#886af7] bg-[#E5DEFF] rounded-xl p-2 w-10 h-10" />
            <h4 className="mt-3 font-serif font-semibold text-lg">Hours</h4>
            <p className="mt-2 text-center font-inter text-gray-700">
              Mon-Sun: 9am - 11pm<br />
              Always available for you
            </p>
          </div>
        </div>
      </section>

      {/* Floating Animations CSS */}
      <style jsx global>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        .float-animation {
          animation: float 6s ease-in-out infinite;
        }
        @keyframes blob {
          0%, 100% { border-radius: 40% 60% 70% 30% / 40% 40% 60% 60%; }
          50% { border-radius: 70% 30% 40% 60% / 50% 60% 40% 50%; }
        }
        .blob-animation {
          animation: blob 15s infinite;
        }
      `}</style>
    </div>
  );
}
